import React from "react";
import { alpha, keyframes, styled } from "@mui/material";

const dotAppearSpeed = 0.08;
const dotBounceSpeed = 2.5;

const createDelayClasses = (count: number, delayIncrement = 0.2) =>
  Object.fromEntries(
    Array.from({ length: count }, (_, i) => [
      `&.dotc_${i + 1}`,
      { animationDelay: `${i * delayIncrement}s` },
    ])
  );

const DotsContainer = styled("div")(({ theme }) => {
  const bellBounce = keyframes`
    0%, 100% {
        transform: scale(1);
        opacity: 1;
        background-color: #f6f8fa;
    }
    50% {
        transform: scale(1.1);
        opacity: 1;
        background-color: ${theme.palette.primary.main};
    }
`;

  return {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    gap: 5.5,

    ".error_container": {
      display: "flex",
      position: "absolute",
      alignItems: "center",
      justifyContent: "center",
      gap: 5.5,
      width: 57,
      height: 57,
      borderRadius: "50%",
      backgroundColor: alpha("#ff534a", 0.3),
      [theme.breakpoints.down("md")]: {
        width: 38,
        height: 38,
      },
      ".error_icon": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 38,
        height: 38,
        [theme.breakpoints.down("md")]: {
          width: 28,
          height: 28,
        },
        backgroundColor: "#ff534a",
        borderRadius: "50%",
      },
    },

    ".dot_col": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 5.5,
    },
    ".dot": {
      width: 15.2,
      height: 15.2,
      borderRadius: "50%",
      backgroundColor: "#f6f8fa",

      [theme.breakpoints.down("md")]: {
        width: 10,
        height: 10,
      },

      "&.animated": {
        animation: `${bellBounce} ${dotBounceSpeed}s ease-in-out infinite`,
      },

      ...createDelayClasses(25, dotAppearSpeed),
    },
  };
});

const ErrorCrossIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14.756"
      height="14.675"
      viewBox="0 0 14.756 14.675"
    >
      <g data-name="cancel/close">
        <g data-name="Group 418">
          <path
            data-name="Path 435"
            d="M7.316 6.552 12.93.938a.545.545 0 1 0-.758-.785l-.013.013-5.614 5.615L.931.167a.545.545 0 0 0-.772.771l5.615 5.614-5.615 5.615a.545.545 0 0 0 .771.771l5.615-5.614 5.614 5.614a.545.545 0 0 0 .771-.771z"
            transform="translate(.828 .75)"
            style={{ fill: "#fff", stroke: "#fff", strokeWidth: "1.5px" }}
          />
        </g>
      </g>
    </svg>
  );
};
const dotPattern = [1, 3, 2, 3, 2, 3, 2, 3, 2, 3, 1];

const DotAnimation = ({
  loading = true,
  error,
}: {
  loading: boolean;
  error: boolean;
}) => {
  // Pre-calculate the position where each column starts
  const columnStarts = dotPattern.reduce((acc, _, index) => {
    acc[index] = index === 0 ? 1 : acc[index - 1] + dotPattern[index - 1];
    return acc;
  }, [] as number[]);

  return (
    <DotsContainer>
      {error && (
        <div className="error_container">
          <div className="error_icon">
            <ErrorCrossIcon />
          </div>
        </div>
      )}
      {dotPattern.map((dot, colIndex) => (
        <div key={colIndex} className="dot_col">
          {Array.from({ length: dot }).map((_, rowIndex) => {
            const dotPosition = columnStarts[colIndex] + rowIndex;
            return (
              <div
                key={rowIndex}
                className={`dot dotc_${dotPosition} ${
                  loading && !error ? "animated" : ""
                }`}
              />
            );
          })}
        </div>
      ))}
    </DotsContainer>
  );
};

export default DotAnimation;
